import { assert } from 'ts-essentials';
import { computed, observable } from 'mobx';
import { DocCreateStatus, IChunksGenerator } from '../interfaces/chunks-generator.interface';

export class ChunkDataManager<Data> {
    private chunkPromises: Map<number, Promise<Data>> = new Map();
    private chunkResolvers: Map<number, (data: Data) => void> = new Map();
    private chunkRejectors: Map<number, (err: Error) => void> = new Map();

    public add(chunkIndex: number, ...chunks: Data[]) {
        for (let i = 0; i < chunks.length; i++) {
            this.alloc(chunkIndex + i);
            this.chunkResolvers.get(chunkIndex + i)?.(chunks[i]);
        }
    }

    public async consume(chunkIndex: number) {
        const chunk = await this.get(chunkIndex);
        this.delete(chunkIndex);
        return chunk;
    }

    public get(chunkIndex: number) {
        this.alloc(chunkIndex);
        return this.chunkPromises.get(chunkIndex);
    }

    public delete(chunkIndex: number) {
        this.chunkPromises.delete(chunkIndex);
        this.chunkResolvers.delete(chunkIndex);
        this.chunkRejectors.delete(chunkIndex);
    }

    public toChunksGenerator(): IChunksGenerator<Data> {
        return new ChunkGenerator(this);
    }

    public dispose(err?: Error) {
        if (err) {
            this.chunkRejectors.forEach(reject => reject(err));
        }
        this.chunkPromises.clear();
        this.chunkResolvers.clear();
        this.chunkRejectors.clear();
    }

    private alloc(chunkIndex: number) {
        if (!this.chunkPromises.has(chunkIndex)) {
            this.chunkPromises.set(
                chunkIndex,
                new Promise((resolve, reject) => {
                    this.chunkResolvers.set(chunkIndex, resolve);
                    this.chunkRejectors.set(chunkIndex, reject);
                }),
            );
        }
    }
}

class ChunkGenerator<Data> implements IChunksGenerator<Data> {
    @observable
    public index = 0;

    private initPromise: Promise<void>;
    private initPromiseResolver: () => void;
    @observable
    private totalChunks = NaN;

    // 文档状态(文档数据是否已落盘)
    #docCreateStatus = DocCreateStatus.AlreadyCreate;
    // 携带完整数据的页数
    #fullPageCount = 0;

    public constructor(private chunks: ChunkDataManager<Data>) {
        this.initPromise = new Promise((resolve) => {
            this.initPromiseResolver = resolve;
        });
    }

    public init(total: number, fullPageCount?: number) {
        this.totalChunks = total;
        total > 0 && (this.#fullPageCount = fullPageCount ?? 1);
        this.initPromiseResolver();
        return this;
    }

    @computed
    public get total() {
        assert(!Number.isNaN(this.totalChunks));
        return this.totalChunks;
    }

    @computed
    public get done() {
        if (Number.isNaN(this.totalChunks)) {
            return false;
        }

        return this.index >= this.totalChunks;
    }

    public get fullPageCount() {
        return this.#fullPageCount;
    }

    public get docCreateStatus() {
        return this.#docCreateStatus;
    }

    public updateTotal(total: number) {
        this.totalChunks = total;
        return this;
    }

    public updateDocCreateStatus(docCreateStatus: DocCreateStatus) {
        this.#docCreateStatus = docCreateStatus;
        return this;
    }

    public advance() {
        this.index += 1;
        return this;
    }

    public async next() {
        await this.initPromise;

        if (this.done) {
            this.chunks.dispose();
            return {
                done: this.done,
                docCreateStatus: this.#docCreateStatus,
            };
        }

        const chunk = await this.chunks.consume(this.index);
        this.advance();

        if (this.done) {
            this.chunks.dispose();
        }

        return {
            chunk,
            done: this.done,
            docCreateStatus: this.#docCreateStatus,
        };
    }
}
